import { useEffect, useState } from 'react';
import { ESSENTIAL_DISCOUNT, PER_DAY } from '../constants/spread';
import { getListPrice } from 'src/common/hooks/widgets/fetchData';
import {
  INGESTION_TIERS,
  MAX_INGESTION,
  MIN_INGESTION,
  TIER1,
  TIER1001,
  TIER2001,
  TIER201,
  TIER301,
  TIER401,
  TIER5001,
  TIER501,
} from '../constants/default';
import { useCurrency } from 'src/common/hooks/widgets/useCurrency';

/**
 * Calculate the estimated price
 *
 * @param {number} ingestion - no of ingestions per GB
 * @param {string} currency - currency based on country
 * @param {string} localeCode - locale based on country/lang
 * @param {string} country - based on ddo
 * @returns {Array}
 */

export interface PropsFunc {
  ingestion: number;
  currency: string;
  localeCode: string;
  country: string;
}

export interface IFetchError {
  status: number;
  statusText: string;
}

interface ScaleQtyPrice {
  qty: number;
  price: number;
}

export interface priceProps {
  partNumber: string;
  pricingIndicator: string;
  pricingModel: number;
  scaleQtyPrice: [
    {
      qty: number;
      price: number;
    },
  ];
}

const partNumber = {
  qradar_cloud_native: 'D0HBVZX',
};

const partsToQuery = [
  {
    partNumber: partNumber.qradar_cloud_native,
  },
];

type MarketPlaceRequest = {
  currency: string;
  countryCode3: string;
  parts: typeof partsToQuery;
};

export const usePrice = ({
  currency,
  ingestion,
  localeCode,
  country,
}: PropsFunc) => {
  const [totalCostPerGB, setTotalCostPerGB] = useCurrency(
    currency,
    localeCode,
    0,
  );
  const [totalCostMonthly, setTotalCostMonthly] = useCurrency(
    currency,
    localeCode,
    0,
  );
  const [tierPrices, setTierPrices] = useState<number[]>([]);
  const [dataFetched, setDataFetched] = useState([]);
  const [fetchingError, setFetchingError] = useState({});
  const [loading, setLoading] = useState(false);

  const isUndefinedForNumber = (value: number | undefined) => {
    return value ? value : 0;
  };

  const calculatePricing = (qty: number, price: number) => {
    return qty * price;
  };

  const calculatedPrice = () => {
    const price = calculatedTierPrice(ingestion, tierPrices, INGESTION_TIERS);
    const discountedPrice = price * ESSENTIAL_DISCOUNT;
    return discountedPrice;
  };

  /**
   * Calculate the final price according to quantity and price per tier
   *
   * @param {number} ingestion : no of ingestion
   * @param {Price[]} PRICES : Array of prices per tier
   * @param {number[]} INGESTION_QTY : Array of quantities per tier
   * @returns {number}
   */
  const calculatedTierPrice = (
    ingestion: number,
    PRICES: number[],
    INGESTION_QTY: number[],
  ): number => {
    const [
      PRICE1,
      PRICE201,
      PRICE301,
      PRICE401,
      PRICE501,
      PRICE1001,
      PRICE2001,
      PRICE5001,
    ] = PRICES;
    const [, QTY201, QTY301, QTY401, QTY501, QTY1001, QTY2001, QTY5001] =
      INGESTION_QTY;
    let price;
    switch (true) {
      case ingestion >= MIN_INGESTION && ingestion < QTY201:
        price = calculatePricing(ingestion, PRICE1);
        break;
      case ingestion >= QTY201 && ingestion < QTY301:
        price = calculatePricing(ingestion, PRICE201);
        break;
      case ingestion >= QTY301 && ingestion < QTY401:
        price = calculatePricing(ingestion, PRICE301);
        break;
      case ingestion >= QTY401 && ingestion < QTY501:
        price = calculatePricing(ingestion, PRICE401);
        break;
      case ingestion >= QTY501 && ingestion < QTY1001:
        price = calculatePricing(ingestion, PRICE501);
        break;
      case ingestion >= QTY1001 && ingestion < QTY2001:
        price = calculatePricing(ingestion, PRICE1001);
        break;
      case ingestion >= QTY2001 && ingestion < QTY5001:
        price = calculatePricing(ingestion, PRICE2001);
        break;
      case ingestion >= QTY5001 && ingestion <= MAX_INGESTION:
        price = calculatePricing(ingestion, PRICE5001);
        break;
      default:
        price = 0;
        break;
    }
    return price;
  };

  // Assigning tiers with their prices
  const setPricesData = (products: priceProps[]) => {
    if (products?.length) {
      const PRICE = products.find(
        (i) => i.partNumber == partNumber.qradar_cloud_native,
      );

      const TIER1_PRICE = PRICE?.scaleQtyPrice.find(
        (i: ScaleQtyPrice) => i.qty == TIER1,
      )?.price;
      const TIER201_PRICE = PRICE?.scaleQtyPrice.find(
        (i: ScaleQtyPrice) => i.qty == TIER201,
      )?.price;
      const TIER301_PRICE = PRICE?.scaleQtyPrice.find(
        (i: ScaleQtyPrice) => i.qty == TIER301,
      )?.price;
      const TIER401_PRICE = PRICE?.scaleQtyPrice.find(
        (i: ScaleQtyPrice) => i.qty == TIER401,
      )?.price;
      const TIER501_PRICE = PRICE?.scaleQtyPrice.find(
        (i: ScaleQtyPrice) => i.qty == TIER501,
      )?.price;
      const TIER1001_PRICE = PRICE?.scaleQtyPrice.find(
        (i: ScaleQtyPrice) => i.qty == TIER1001,
      )?.price;
      const TIER2001_PRICE = PRICE?.scaleQtyPrice.find(
        (i: ScaleQtyPrice) => i.qty == TIER2001,
      )?.price;
      const TIER5001_PRICE = PRICE?.scaleQtyPrice.find(
        (i: ScaleQtyPrice) => i.qty == TIER5001,
      )?.price;
      const TIER_PRICES: number[] = [
        isUndefinedForNumber(TIER1_PRICE),
        isUndefinedForNumber(TIER201_PRICE),
        isUndefinedForNumber(TIER301_PRICE),
        isUndefinedForNumber(TIER401_PRICE),
        isUndefinedForNumber(TIER501_PRICE),
        isUndefinedForNumber(TIER1001_PRICE),
        isUndefinedForNumber(TIER2001_PRICE),
        isUndefinedForNumber(TIER5001_PRICE),
      ];
      setTierPrices(TIER_PRICES);
    }
  };

  const fetchPrices = () => {
    setLoading(true);
    const body: MarketPlaceRequest = {
      currency: currency, // to be replaced by currency once globalized
      countryCode3: country, // to be replaced by country once globalized
      parts: partsToQuery,
    };
    getListPrice(
      `https://api.marketplace.ibm.com/purchase/catalog/resources/price/info`,
      JSON.stringify(body),
      'POST',
    )
      .then((data) => {
        if (data.parts && data.parts.length !== 0) {
          setLoading(false);
          setPricesData(data.parts);
          setDataFetched(data.parts);
        } else {
          setLoading(false);
          setDataFetched([]);
        }
      })
      .catch((error) => {
        setLoading(false);
        setDataFetched([]);
        if (error.response) {
          setFetchingError(error.response);
        }
      });
  };

  const price = calculatedPrice();

  useEffect(() => {
    if (currency && dataFetched.length === 0) {
      fetchPrices();
    }
  }, [localeCode, currency, country]);

  useEffect(() => {
    setTotalCostPerGB(ingestion > 0 ? (price * PER_DAY) / ingestion : 0);
    setTotalCostMonthly(Math.round(price));
  }, [ingestion, tierPrices]);

  return [totalCostPerGB, totalCostMonthly, loading, fetchingError];
};
